import { Fragment, appendDelta } from "./liveTranscript";

export type CleanSpeaker = "user" | "assistant";

export interface CleanRequest {
  speaker: CleanSpeaker;
  text: string;
  context: string;
  seq: number;
  final?: boolean;
}

export interface CleanedText {
  text: string;
  complete: boolean;
  final: boolean;
}

export type CleanLog = (step: string, payload: Record<string, unknown>) => void;

interface Batch {
  first: Fragment | null;
  line: Fragment[];
  speaker: CleanSpeaker;
  raw: string;
  seq: number;
  sentAt: number;
  cleaned: string | null;
  showsRaw: boolean;
  done: boolean;
}

const rawText = (line: Fragment[], fragments: Fragment[]): string => {
  const index = line.indexOf(fragments[0]);
  const before = index > 0 ? line[index - 1].text : "";
  return fragments.reduce(appendDelta, before).slice(before.length);
};

const shown = (batch: Batch): string | null => batch.cleaned ?? (batch.showsRaw ? batch.raw : null);

export class TranscriptCleaner {
  private batches = new WeakMap<Fragment, Batch>();
  private tails = new WeakMap<Fragment, Batch>();
  private closed = new WeakSet<Fragment>();
  private waiting = new Set<Batch>();
  private seq = 0;
  private lastSentAt = -Infinity;
  private send: (request: CleanRequest) => Promise<string>;
  private onCleaned: () => void;
  private log: CleanLog;
  private cadenceMs: number;
  private waitMs: number;

  constructor(
    send: (request: CleanRequest) => Promise<string>,
    onCleaned: () => void,
    log: CleanLog,
    cadenceMs: number,
    waitMs: number,
  ) {
    this.send = send;
    this.onCleaned = onCleaned;
    this.log = log;
    this.cadenceMs = cadenceMs;
    this.waitMs = waitMs;
  }

  cleaned(fragments: Fragment[]): CleanedText {
    let text = "";
    let complete = true;
    let final = true;
    fragments.forEach((fragment) => {
      const batch = this.batches.get(fragment);
      const piece = batch ? shown(batch) : null;
      if (piece === null) complete = false;
      else if (batch?.first === fragment) text += piece;
      if (!batch?.done) final = false;
      const tail = this.tails.get(fragment);
      if (!tail) return;
      const end = shown(tail);
      if (end === null) complete = false;
      else text += end;
      if (!tail.done) final = false;
    });
    return { text: text.trim(), complete, final };
  }

  best(fragments: Fragment[]): string {
    return this.withRaw(fragments).trim();
  }

  finish(speaker: CleanSpeaker, line: Fragment[]): void {
    const last = line[line.length - 1];
    const rest = line.filter((fragment) => !this.batches.has(fragment));
    if (!last || (rest.length === 0 && this.closed.has(last))) return;
    this.closed.add(last);
    this.start(speaker, line, rest, true);
  }

  flush(speaker: CleanSpeaker, line: Fragment[]): void {
    const rest = line.filter((fragment) => !this.batches.has(fragment));
    if (rest.length > 0) this.start(speaker, line, rest);
  }

  forget(fragments: Fragment[]): void {
    fragments.forEach((fragment) => {
      const batch = this.batches.get(fragment);
      if (batch) this.waiting.delete(batch);
      this.batches.delete(fragment);
      this.tails.delete(fragment);
      this.closed.delete(fragment);
    });
  }

  pump(live: Fragment[], at: number): void {
    const fresh = live.filter((fragment) => !this.batches.has(fragment));
    if (fresh.length === 0 || at - this.lastSentAt < this.cadenceMs) return;
    this.lastSentAt = at;
    this.start("user", live, fresh);
  }

  private withRaw(fragments: Fragment[]): string {
    let text = "";
    let unsent: Fragment[] = [];
    fragments.forEach((fragment) => {
      const batch = this.batches.get(fragment);
      if (!batch) {
        unsent.push(fragment);
        return;
      }
      if (unsent.length > 0) text += rawText(fragments, unsent);
      unsent = [];
      if (batch.first === fragment) text += batch.cleaned ?? batch.raw;
      const tail = this.tails.get(fragment);
      if (tail) text += tail.cleaned ?? tail.raw;
    });
    return unsent.length > 0 ? text + rawText(fragments, unsent) : text;
  }

  private start(speaker: CleanSpeaker, line: Fragment[], fragments: Fragment[], final = false): void {
    const raw = rawText(line, fragments);
    const batch: Batch = {
      first: fragments[0] ?? null,
      line,
      speaker,
      raw,
      seq: 0,
      sentAt: Date.now(),
      cleaned: null,
      showsRaw: false,
      done: false,
    };
    fragments.forEach((fragment) => this.batches.set(fragment, batch));
    if (!final && !raw.trim()) {
      batch.cleaned = raw;
      batch.done = true;
      this.onCleaned();
      return;
    }
    const context = this.withRaw(batch.first ? line.slice(0, line.indexOf(batch.first)) : line);
    if (!batch.first) this.tails.set(line[line.length - 1], batch);
    this.seq += 1;
    batch.seq = this.seq;
    this.waiting.add(batch);
    setTimeout(() => {
      if (this.fallBack(batch, "timeout")) this.onCleaned();
    }, this.waitMs);
    this.send({ speaker, text: raw, context, seq: batch.seq, ...(final ? { final } : {}) }).then(
      (cleaned) => this.answered(batch, cleaned, null),
      (error) => this.answered(batch, null, error),
    );
  }

  private answered(batch: Batch, cleaned: string | null, error: any): void {
    batch.done = true;
    if (cleaned !== null) {
      if (batch.showsRaw) this.log("clean.late", { seq: batch.seq, speaker: batch.speaker, ms: Date.now() - batch.sentAt });
      batch.cleaned = cleaned;
      this.waiting.delete(batch);
    } else if (this.waiting.delete(batch)) {
      batch.showsRaw = true;
      this.log("clean.failed", { seq: batch.seq, speaker: batch.speaker, reason: "error", ms: Date.now() - batch.sentAt, message: error?.message });
    }
    const at = batch.first ? batch.line.indexOf(batch.first) : batch.line.length;
    this.waiting.forEach((other) => {
      const index = other.first ? batch.line.indexOf(other.first) : -1;
      if (index >= 0 && index < at) this.fallBack(other, "overtaken");
    });
    this.onCleaned();
  }

  private fallBack(batch: Batch, reason: "overtaken" | "timeout"): boolean {
    if (!this.waiting.delete(batch)) return false;
    batch.showsRaw = true;
    this.log("clean.failed", { seq: batch.seq, speaker: batch.speaker, reason, ms: Date.now() - batch.sentAt });
    return true;
  }
}

export async function readTranscriptFrame(response: Response, seq: number): Promise<string | null> {
  if (!response.ok || !response.body) return null;
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  for (;;) {
    const { done, value } = await reader.read();
    if (done) return null;
    buffer = (buffer + decoder.decode(value, { stream: true })).split("\r\n").join("\n");
    for (let boundary = buffer.indexOf("\n\n"); boundary !== -1; boundary = buffer.indexOf("\n\n")) {
      const data = buffer
        .slice(0, boundary)
        .split("\n")
        .filter((line) => line.startsWith("data:"))
        .map((line) => line.slice(5).trim())
        .join("\n");
      buffer = buffer.slice(boundary + 2);
      let frame: any = null;
      try {
        frame = data ? JSON.parse(data) : null;
      } catch {}
      if (frame?.type === "transcript" && (frame.seq === undefined || frame.seq === seq)) {
        void reader.cancel();
        return typeof frame.text === "string" ? frame.text : "";
      }
    }
  }
}
